import React, { useEffect } from "react";

import MuiAutocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";

const Autocomplete = ({
    simpleValue,
    fetchOptions,
    value,
    valueLabel,
    onChange,
    label,
    placeholder,
    id,
    error,
    disabled,
    readOnly,
    helperText,
    loadingText,
    getOptionLabel,
    variant,
    margin,
    fullWidth,
    filterOptions,
    freeSolo,
    required,
    ...props
}) => {
    const [open, setOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState("");
    const [options, setOptions] = React.useState([]);
    const [loading, setLoading] = React.useState(open && options.length === 0);

    useEffect(() => {
        if (!value) {
            setInputValue("");
            return;
        }
        if (simpleValue) {
            setInputValue(valueLabel);
        } else {
            setInputValue(getOptionLabel(value));
        }
    }, [value, simpleValue, getOptionLabel, valueLabel]);

    useEffect(() => {
        let active = true;

        if (open && active && fetchOptions) {
            (async () => {
                try {
                    setLoading(true);
                    let options = [];
                    if (typeof fetchOptions == "function") {
                        options = await fetchOptions(inputValue);
                    } else {
                        options = await fetchOptions;
                    }

                    if (active) {
                        setOptions(options || []);
                    }
                } catch (e) {
                    console.error(e);
                } finally {
                    setLoading(false);
                }
            })();
        } else {
            setOptions([]);
        }

        return () => {
            active = false;
        };
    }, [open, inputValue]);

    return (
        <MuiAutocomplete
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            getOptionLabel={getOptionLabel || ((option) => option.label)}
            options={options}
            loading={loading}
            value={value}
            onChange={(e, newValue) => onChange?.(newValue)}
            variant={variant || "standard"}
            disabled={disabled}
            readOnly={readOnly}
            loadingText={loadingText}
            onInputChange={(_, newInputValue) => {
                setInputValue(newInputValue);
            }}
            filterOptions={filterOptions}
            freeSolo={freeSolo}
            renderInput={(params) => (
                <TextField
                    {...params}
                    disabled={disabled}
                    readOnly={readOnly}
                    label={label}
                    fullWidth={fullWidth}
                    margin={margin}
                    helperText={helperText}
                    error={!!error}
                    variant={variant}
                    required={required}
                    placeholder={placeholder || params.placeholder}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {disabled || readOnly ? null : params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
            {...props}
        />
    );
};

export default Autocomplete;
